import { createStore } from 'vuex'
import { StationList, USE_MICROGRID_MOCK, resolveDataLocation } from '../constants'
import { buildMicrogridColumns } from '../mock/microgrid'
import {
    MICROGRID_FIELDS,
    findLastOnlineTime,
    isMicrogridOffline,
    maskOfflineSamples,
} from '../microgrid'

const API_BASE = process.env.VUE_APP_API_BASE_URL || 'https://weather.ecusr.ncu.edu.tw/api'

// 綠能自給率為推導值，不存在於原始資料中，故在資料組裝階段補上。
// 真實 API 接上後這段照樣適用，不需改動。
function addGreenRatio(grouped) {
    const pv = grouped['todaypvpower']
    const load = grouped['todayloadpower']
    if (!Array.isArray(pv) || !Array.isArray(load)) return grouped

    grouped['GreenRatio'] = pv.map((pvValue, i) => {
        const loadValue = load[i]
        // 每日剛過午夜時用電量為 0，此時自給率無意義，回傳 null 讓 UI 顯示「—」
        if (!loadValue || typeof pvValue !== 'number') return null
        return Math.round((pvValue / loadValue) * 1000) / 10
    })
    return grouped
}

// 每度電的排碳量（kgCO₂e/kWh）。太陽能發的每一度都取代了一度市電，
// 故減碳量即為發電量乘上這個係數。
const CARBON_PER_KWH = 0.467
// 一平方公尺桂竹林一天的固碳量換算：每 kWh 的減碳量相當於這麼多平方公尺。
const BAMBOO_M2_PER_KWH = 76.37

// 減碳成效同為推導值，由今日發電量換算而來，不存在於原始資料中。
function addCarbonImpact(grouped) {
    const pv = grouped['todaypvpower']
    if (!Array.isArray(pv)) return grouped

    grouped['CarbonReduced'] = pv.map((value) =>
        typeof value === 'number' ? Math.round(value * CARBON_PER_KWH * 100) / 100 : null
    )
    grouped['BambooArea'] = pv.map((value) =>
        typeof value === 'number' ? Math.round(value * BAMBOO_M2_PER_KWH) : null
    )
    return grouped
}

// 圖表橫軸固定顯示最近 12 小時。原本是「撈 48 筆 15 分鐘資料」＝ 12 小時的
// 筆數，但取整點後只剩 12 個點、頭尾相距 11 小時，橫軸看起來就少一小時。
const CHART_WINDOW_HOURS = 12
// 卡片被點開放大時改看 24 小時。原本放大版跟小卡看同一段時間，等於點開只是把
// 同一張圖畫大，資訊量沒有增加。
const ZOOM_WINDOW_HOURS = 24
// 資料是 15 分鐘一筆，24 小時＝96 筆，取 100 給取樣留餘裕（最新一筆可能落在
// :15 或 :45，跟整點對不上）。後端 /history/ 的查詢範圍寫死 before - 1 day，
// 所以 24 小時就是目前拿得到的上限，再往上要動後端。
const HISTORY_LIMIT = 100

/**
 * 算出圖表要取 historyData 的哪幾個索引。
 *
 * 時間軸與各條資料必須取同一組索引，否則兩邊會錯開一格。這段原本在
 * getChartTimeSeries 與 getDatasets 各寫一份，只靠註解提醒要一起改。
 *
 * @param {object} state vuex state
 * @returns {number[]} 由舊到新的索引
 */
function chartSampleIndices(state) {
    const times = state.historyData['DateTime']
    if (!times) return []

    const indices = []
    times.forEach((timeStr, index) => {
        const minutes = new Date(timeStr).getMinutes()
        if (minutes === 0 || (minutes === 30 && state.chartTimeInterval === 30)) {
            indices.push(index)
        }
    })
    // 取樣後的點數會隨最新一筆的分鐘數浮動，多的裁掉，橫軸長度才固定。
    // 12 小時要 13 個整點（或 25 個半點），首尾各算一個。
    // 放大時（largeCardId 不是 'None'）畫面上只剩那一張卡，整段換成 24 小時。
    const hours = state.largeCardId === 'None' ? CHART_WINDOW_HOURS : ZOOM_WINDOW_HOURS
    const wanted = (hours * 60) / state.chartTimeInterval + 1
    return indices.slice(-wanted)
}

export default createStore({
    state: {
        // data
        // dynamic data
        historyFetched: false,
        selectedDate: new Date(),
        selectedStation: 'ys',
        chartTimeInterval: 60, // minutes, related to getChartTimeSeries and getDatasets
        timeSeriesList: [],
        latestData: {
            DateTime: '1970-01-01T00:00:00.00000',
            SolarRad: 0,
            WindSpeed: 0,
            WindDir: 'N',
            TempIn: 0,
            TempOut: 0,
            RainDay: 0,
            RainMonth: 0,
            RainYear: 0,
            RainHour: 0,
            Barometer: 0,
            HumIn: 0,
            HumOut: 0,
            GPSLongitude: 0,
            GPSLatitude: 0,
            GPSHeight: 0,
            Turbidity: 0,
            THW_index: 0,
            dt_x: 0,
            dt_y: 0,
        },
        historyData: [],
        gpsPivot: {
            AvgLongitude: 0,
            AvgLatitude: 0,
        },
        // layout
        largeCardId: 'None',
        mobileCardId: 'solar',
        firstCardIndex: {
            small: 0,
            medium: 0,
        },
        // static data
        cardList: [
            {
                id: 'solar',
                cardSize: 'small',
                cardIcon: 'fa-solid fa-sun',
                chartType: 'line',
                chartOptions: {
                    scales: {
                        y: {
                            title: {
                                display: true,
                                font: {
                                    size: 12,
                                },
                                text: 'scales.solar-y',
                            },
                            min: 0,
                        },
                    },
                },
                data: [
                    {
                        id: 'SolarRad',
                        text: 'data-name.SolarRad',
                        unit: 'W/㎡',
                        round: 1,
                        chartSettings: {
                            label: 'data-name.SolarRad',
                            backgroundColor: '#D6B84B',
                            borderColor: '#D6B84B',
                            borderWidth: 2,
                            fill: false,
                            pointBackgroundColor: '#D6B84B',
                        },
                    },
                ],
            },
            {
                id: 'wind',
                cardSize: 'small',
                cardIcon: 'fa-solid fa-wind',
                chartType: 'wind',
                data: [
                    { id: 'WindSpeed', text: 'data-name.WindSpeed', unit: 'm/s' },
                    { id: 'WindDir', text: 'data-name.WindDir', unit: '' },
                ],
            },
            {
                id: 'rain',
                cardSize: 'small',
                cardIcon: 'fa-solid fa-cloud-showers-heavy',
                chartType: 'bar',
                data: [
                    { id: 'RainDay', text: 'data-name.RainDay', unit: 'mm' },
                    { id: 'RainMonth', text: 'data-name.RainMonth', unit: 'mm' },
                    { id: 'RainYear', text: 'data-name.RainYear', unit: 'mm' },
                    // 時雨量改用後端以累積雨量相減算出的 RainHour。廠商原本的
                    // RainRate 是翻斗瞬時值，15 分鐘取樣一次幾乎必定取到 0，
                    // 圖表因此長期是一條貼底的零線。
                    {
                        id: 'RainHour',
                        text: 'data-name.RainHour',
                        unit: 'mm',
                        chartSettings: {
                            label: 'data-name.RainHour',
                            backgroundColor: '#5E6BA6',
                            borderColor: '#5E6BA6',
                            borderWidth: 1,
                        },
                    },
                ],
            },
            {
                id: 'pressure',
                cardSize: 'small',
                // cardIcon: 'fa-solid fa-arrows-down-to-line',
                chartType: 'line',
                chartOptions: {
                    scales: {
                        y: {
                            title: {
                                display: true,
                                font: {
                                    size: 12,
                                },
                                text: 'scales.pressure-y',
                            },
                        },
                    },
                },
                data: [
                    {
                        id: 'Barometer',
                        text: 'data-name.Barometer',
                        unit: 'hPa',
                        round: 1,
                        chartSettings: {
                            label: 'data-name.Barometer',
                            backgroundColor: '#D6B84B',
                            borderColor: '#D6B84B',
                            borderWidth: 2,
                            fill: false,
                            pointBackgroundColor: '#D6B84B',
                        },
                    },
                ],
            },
            {
                id: 'temp',
                cardSize: 'medium',
                cardIcon: 'fa-solid fa-temperature-full',
                chartType: 'line',
                chartOptions: {
                    plugins: {
                        legend: {
                            display: true,
                        },
                        tooltip: {
                            callbacks: {
                                label: (data) => {
                                    const yVal = Math.round(data.parsed.y * 10) / 10
                                    return `${data.dataset.label}: ${yVal}°C`
                                },
                            },
                        },
                    },
                    scales: {
                        y: {
                            title: {
                                display: true,
                                font: {
                                    size: 12,
                                },
                                text: 'scales.temp-y',
                            },
                        },
                    },
                },
                data: [
                    {
                        id: 'TempIn',
                        text: 'data-name.TempIn',
                        unit: '°C',
                        round: 1,
                        chartSettings: {
                            label: 'data-name.TempIn',
                            backgroundColor: '#D6B84B',
                            borderColor: '#D6B84B',
                            borderWidth: 2,
                            fill: false,
                        },
                    },
                    {
                        id: 'TempOut',
                        text: 'data-name.TempOut',
                        unit: '°C',
                        round: 1,
                        chartSettings: {
                            label: 'data-name.TempOut',
                            backgroundColor: '#D8894A',
                            borderColor: '#D8894A',
                            borderWidth: 2,
                            fill: false,
                        },
                    },
                    {
                        id: 'THW_index',
                        text: 'data-name.THW_index',
                        unit: '°C',
                        round: 1,
                        chartSettings: {
                            label: 'data-name.THW_index',
                            backgroundColor: '#78464D',
                            borderColor: '#78464D',
                            borderWidth: 2,
                            fill: false,
                        },
                    },
                ],
            },
            {
                id: 'humid',
                cardSize: 'medium',
                cardIcon: 'fa-solid fa-droplet',
                chartType: 'line',
                chartOptions: {
                    plugins: {
                        legend: {
                            display: true,
                        },
                    },
                    scales: {
                        y: {
                            title: {
                                display: true,
                                font: {
                                    size: 12,
                                },
                                text: 'scales.humid-y',
                            },
                        },
                    },
                },
                data: [
                    {
                        id: 'HumIn',
                        text: 'data-name.HumIn',
                        unit: '%',
                        chartSettings: {
                            label: 'data-name.HumIn',
                            backgroundColor: '#8BA7C2',
                            borderColor: '#8BA7C2',
                            borderWidth: 2,
                            fill: false,
                        },
                    },
                    {
                        id: 'HumOut',
                        text: 'data-name.HumOut',
                        unit: '%',
                        chartSettings: {
                            label: 'data-name.HumOut',
                            backgroundColor: '#5E6BA6',
                            borderColor: '#5E6BA6',
                            borderWidth: 2,
                            fill: false,
                        },
                    },
                ],
            },
            {
                id: 'gps',
                cardSize: 'medium',
                cardIcon: 'fa-solid fa-arrows-up-down-left-right',
                chartType: 'scatter',
                chartOptions: {
                    plugins: {
                        legend: {
                            display: true,
                            onClick: null,
                        },
                        tooltip: {
                            callbacks: {
                                label: (data) => {
                                    // remove text inside parentheses
                                    const labelTitle = data.dataset.label.replace(/\(.+\)/, '')
                                    // data round to 2 decimal places
                                    const x = Math.round(data.parsed.x * 100) / 100
                                    const y = Math.round(data.parsed.y * 100) / 100
                                    return `${labelTitle}: (${x}, ${y})`
                                },
                            },
                        },
                    },
                    scales: {
                        x: {
                            suggestedMin: -10,
                            suggestedMax: 10,
                            title: {
                                display: true,
                                font: {
                                    size: 12,
                                },
                                text: 'scales.gps-x',
                            },
                        },
                        y: {
                            suggestedMin: -5,
                            suggestedMax: 5,
                            title: {
                                display: true,
                                font: {
                                    size: 12,
                                },
                                text: 'scales.gps-y',
                            },
                        },
                    },
                },
                data: [
                    {
                        id: 'GPSLongitude',
                        text: 'data-name.GPSLongitude',
                        unit: '°',
                        chartSettings: {},
                    },
                    {
                        id: 'GPSLatitude',
                        text: 'data-name.GPSLatitude',
                        unit: '°',
                        chartSettings: {},
                    },
                    { id: 'GPSHeight', text: 'data-name.GPSHeight', unit: 'm' },
                ],
            },
            {
                id: 'water',
                cardSize: 'small',
                cardIcon: 'fa-solid fa-glass-water',
                chartType: 'bar',
                // 濁度分級（gaugeBands.js 的 turbidity）已經備好，要把水杯換成指針儀表
                // 只需補上這一行：
                //   gauge: { dataId: 'Turbidity', bands: 'turbidity', unit: 'NTU', withChart: true },
                // 目前維持水杯圖示，下方另外畫趨勢圖。
                chartOptions: {
                    scales: {
                        y: {
                            title: {
                                display: true,
                                font: {
                                    size: 12,
                                },
                                text: 'scales.turbidity-y',
                            },
                            min: 0,
                        },
                    },
                },
                data: [
                    {
                        id: 'Turbidity',
                        text: 'data-name.Turbidity',
                        unit: 'NTU',
                        // 濁度原本沒有 chartSettings，所以這張卡一直只有水杯沒有圖表。
                        // getDatasets 只畫有設定的欄位。
                        chartSettings: {
                            label: 'data-name.Turbidity',
                            backgroundColor: '#3E9BC7',
                            borderColor: '#3E9BC7',
                            borderWidth: 0,
                            borderRadius: 2,
                            maxBarThickness: 14,
                        },
                    },
                ],
            },
            {
                id: 'air_gas',
                cardSize: 'medium',
                cardIcon: 'fa-solid fa-flag',
                // 逐時濃度是一段一段的觀測值，不是連續變化的量，用長條比折線誠實
                chartType: 'bar',
                // bands 指向 gaugeBands.js 的分級表；withChart 表示儀表與趨勢圖同時顯示
                gauge: { dataId: 'CO2', bands: 'co2', unit: 'ppm', withChart: true },
                chartOptions: {
                    scales: {
                        y: {
                            title: {
                                display: true,
                                font: {
                                    size: 12,
                                },
                                text: 'scales.air-y',
                            },
                            min: 0,
                        },
                    },
                },
                data: [
                    {
                        id: 'CO2',
                        text: 'data-name.CO2',
                        unit: 'ppm',
                        round: 1,
                        chartSettings: {
                            label: 'data-name.CO2',
                            backgroundColor: '#D6B84B',
                            borderColor: '#D6B84B',
                            borderWidth: 0,
                            borderRadius: 2,
                            maxBarThickness: 14,
                        },
                    },
                ],
            },
            {
                id: 'air_particle',
                cardSize: 'medium',
                cardIcon: 'fa-regular fa-flag',
                chartType: 'bar',
                gauge: { dataId: 'Pm2_5', bands: 'pm25', unit: 'μg/m³', withChart: true },
                chartOptions: {
                    scales: {
                        y: {
                            title: {
                                display: true,
                                font: {
                                    size: 12,
                                },
                                text: 'scales.particle-y',
                            },
                            min: 0,
                        },
                    },
                },
                data: [
                    {
                        id: 'Pm2_5',
                        text: 'data-name.Pm2_5',
                        unit: 'μg/m³',
                        round: 1,
                        chartSettings: {
                            label: 'data-name.Pm2_5',
                            backgroundColor: '#5E6BA6',
                            borderColor: '#5E6BA6',
                            borderWidth: 0,
                            borderRadius: 2,
                            maxBarThickness: 14,
                        },
                    },
                ],
            },
            {
                id: 'tilt',
                cardSize: 'medium',
                cardIcon: 'fa-solid fa-ruler-combined',
                chartType: 'line',
                chartOptions: {
                    plugins: {
                        legend: {
                            display: true,
                        },

                        tooltip: {
                            callbacks: {
                                label: (data) => {
                                    const val = Math.round(data.parsed.y * 10) / 10
                                    return `${data.dataset.label}: ${val}${
                                        data.dataset.yAxisID === 'y1' ? '°C' : '°'
                                    }`
                                },
                            },
                        },
                    },
                    scales: {
                        y: {
                            position: 'left',
                            title: {
                                display: true,
                                text: 'scales.dt-y',
                                font: {
                                    size: 12,
                                },
                            },

                            min: -10,
                            max: 10,
                        },
                    },
                },
                data: [
                    {
                        id: 'dt_x',
                        text: 'data-name.dt_x',
                        unit: '°',
                        round: 1,
                        chartSettings: {
                            label: 'data-name.dt_x',
                            backgroundColor: '#8BA7C2',
                            borderColor: '#8BA7C2',
                            borderWidth: 2,
                            fill: false,
                            // pointRadius: 4,
                            pointBackgroundColor: '#8BA7C2',
                            yAxisID: 'y', // ← 左側傾角 Y 軸
                        },
                    },
                    {
                        id: 'dt_y',
                        text: 'data-name.dt_y',
                        unit: '°',
                        round: 1,
                        chartSettings: {
                            label: 'data-name.dt_y',

                            backgroundColor: '#5E6BA6',
                            borderColor: '#5E6BA6',

                            borderWidth: 2,
                            fill: false,
                            // pointRadius: 4,
                            pointBackgroundColor: '#5E6BA6',
                            yAxisID: 'y', // ← 左側傾角 Y 軸
                        },
                    },
                ],
            },
            {
                id: 'meteor',
                cardSize: 'medium', // 預設為中型，符合全部模式下排
                cardIcon: 'fa-solid fa-star',
                data: [], // 影像卡片不需要數值資料
            },
            // ---------- 微電網 ----------
            {
                id: 'energy_flow',
                // large：不進「全部總覽」輪播（getCardList 只取 small/medium），
                // 能流圖需要的版面寬度不適合輪播
                cardSize: 'large',
                cardIcon: 'fa-solid fa-plug-circle-bolt',
                chartType: 'energyflow',
                chartOptions: {
                    scales: {
                        y: {
                            title: { display: true, font: { size: 12 }, text: 'scales.power-y' },
                            min: 0,
                        },
                    },
                },
                // 六個欄位全部 hidden：數值已標在能流圖的節點上，
                // 卡片標頭再列一次是重複，而且會壓縮圖形可用高度。
                // 仍必須列在 data 中，否則 getSelectedData 找不到所屬卡片會拋錯。
                data: [
                    {
                        id: 'pvpower',
                        text: 'data-name.pvpower',
                        unit: 'W',
                        round: 0,
                        hidden: true,
                        chartSettings: {
                            label: 'data-name.pvpower',
                            backgroundColor: '#D18800',
                            borderColor: '#D18800',
                            borderWidth: 2,
                            fill: false,
                            pointBackgroundColor: '#D18800',
                        },
                    },
                    {
                        id: 'gridpower',
                        text: 'data-name.gridpower',
                        unit: 'W',
                        round: 0,
                        hidden: true,
                        chartSettings: {
                            label: 'data-name.gridpower',
                            backgroundColor: '#005088',
                            borderColor: '#005088',
                            borderWidth: 2,
                            fill: false,
                            pointBackgroundColor: '#005088',
                        },
                    },
                    {
                        id: 'loadmeteractivepower',
                        text: 'data-name.loadmeteractivepower',
                        unit: 'W',
                        round: 0,
                        hidden: true,
                        chartSettings: {
                            label: 'data-name.loadmeteractivepower',
                            backgroundColor: '#11caa0',
                            borderColor: '#11caa0',
                            borderWidth: 2,
                            fill: false,
                            pointBackgroundColor: '#11caa0',
                        },
                    },
                    {
                        id: 'batterysoc',
                        text: 'data-name.batterysoc',
                        unit: '%',
                        round: 1,
                        hidden: true,
                    },
                    {
                        id: 'chargingpower',
                        text: 'data-name.chargingpower',
                        unit: 'W',
                        round: 0,
                        hidden: true,
                    },
                    {
                        id: 'dischargingpower',
                        text: 'data-name.dischargingpower',
                        unit: 'W',
                        round: 0,
                        hidden: true,
                    },
                ],
            },
            {
                id: 'today_energy',
                cardSize: 'small',
                cardIcon: 'fa-solid fa-leaf',
                chartType: 'line',
                // 未放大時以半圓儀表呈現自給率；放大後改看 chartType 指定的歷史折線。
                // 自給率有天然上限（100%），滿刻度才固定得住，讀者才判斷得出高低。
                gauge: { dataId: 'GreenRatio', min: 0, max: 100, unit: '%' },
                chartOptions: {
                    scales: {
                        y: {
                            title: { display: true, font: { size: 12 }, text: 'scales.energy-y' },
                            min: 0,
                        },
                    },
                },
                data: [
                    // 綠能自給率為推導值（todaypvpower ÷ todayloadpower），單位與下兩者不同，
                    // 故不畫進同一張折線圖。數值已是儀表的主視覺，標頭不再重複列出。
                    {
                        id: 'GreenRatio',
                        text: 'data-name.GreenRatio',
                        unit: '%',
                        round: 1,
                        hidden: true,
                    },
                    {
                        id: 'todaypvpower',
                        text: 'data-name.todaypvpower',
                        unit: 'kWh',
                        round: 2,
                        chartSettings: {
                            label: 'data-name.todaypvpower',
                            backgroundColor: '#D18800',
                            borderColor: '#D18800',
                            borderWidth: 2,
                            fill: false,
                            pointBackgroundColor: '#D18800',
                        },
                    },
                    {
                        id: 'todayloadpower',
                        text: 'data-name.todayloadpower',
                        unit: 'kWh',
                        round: 2,
                        chartSettings: {
                            label: 'data-name.todayloadpower',
                            backgroundColor: '#11caa0',
                            borderColor: '#11caa0',
                            borderWidth: 2,
                            fill: false,
                            pointBackgroundColor: '#11caa0',
                        },
                    },
                ],
            },
            {
                id: 'battery',
                cardSize: 'small',
                cardIcon: 'fa-solid fa-car-battery',
                chartType: 'line',
                // SOC 是「水位」型的有界指標，液面高度直接對應數值，
                // 比一條幾乎不動的折線更有資訊量。放大後仍看歷史折線。
                gauge: { type: 'liquid', dataId: 'batterysoc', min: 0, max: 100, unit: '%' },
                chartOptions: {
                    scales: {
                        y: {
                            title: { display: true, font: { size: 12 }, text: 'scales.soc-y' },
                            min: 0,
                            max: 100,
                        },
                    },
                },
                data: [
                    {
                        id: 'batterysoc',
                        text: 'data-name.batterysoc',
                        unit: '%',
                        round: 1,
                        // 數值已是液面圓的主視覺，標頭不再重複列出
                        hidden: true,
                        chartSettings: {
                            label: 'data-name.batterysoc',
                            backgroundColor: '#7c5cbf',
                            borderColor: '#7c5cbf',
                            borderWidth: 2,
                            fill: false,
                            pointBackgroundColor: '#7c5cbf',
                        },
                    },
                ],
            },
            {
                id: 'carbon',
                cardSize: 'small',
                cardIcon: 'fa-solid fa-seedling',
                // 兩個換算值都是文字敘述，折線或儀表都表達不了「相當於多少桂竹林」，
                // 故與能流圖一樣走自訂元件。
                chartType: 'carbon',
                data: [
                    // 數值已是卡片內文的主視覺，標頭不再重複列出
                    {
                        id: 'CarbonReduced',
                        text: 'data-name.CarbonReduced',
                        unit: 'kgCO₂e',
                        round: 2,
                        hidden: true,
                    },
                    {
                        id: 'BambooArea',
                        text: 'data-name.BambooArea',
                        unit: 'm²',
                        round: 0,
                        hidden: true,
                    },
                ],
            },
        ],
    },
    getters: {
        // computed
        getCard: (state) => (id) => {
            return state.cardList.find((card) => card.id === id)
        },
        getCardList: (state) => (cardSize) => {
            const excludedData = StationList.find(
                (station) => station.id === state.selectedStation
            ).excludedData

            // // 如果是 sg，只顯示傾角儀卡片
            // if (state.selectedStation === 'sg') {
            //     return state.cardList.filter(
            //         (card) => card.id === 'tilt' && card.cardSize === 'medium'
            //     )
            // }
            const cards = state.cardList.filter(
                (card) =>
                    (card.cardSize === cardSize || !cardSize) && !excludedData.includes(card.id)
            )
            return cards
        },
        getDisplayCardList: (state, getters) => (cardSize) => {
            // // 如果是 sg，只顯示一張中型傾斜儀卡片
            // if (state.selectedStation === 'sg' && cardSize === 'medium') {
            //     return getters.getCardList(cardSize)
            // }
            // use for slideshow
            const cards = getters.getCardList(cardSize)
            // 排除小尺寸卡片中的傾斜儀
            // const cards = getters.getCardList(cardSize).filter((card) => {
            //     return !(
            //         state.selectedStation === 'sg' &&
            //         card.id === 'tilt' &&
            //         cardSize === 'small'
            //     )
            // })
            const maxLength = cardSize === 'medium' ? 2 : 4
            // 卡片數不足一個版位時無從輪播，接起來只會讓同一張卡出現兩次
            // （中大白色能源屋只有兩張小卡），v-for 的 key 也會重複。
            if (cards.length <= maxLength) return cards
            return [...cards, ...cards].slice(
                state.firstCardIndex[cardSize],
                state.firstCardIndex[cardSize] + maxLength
            )
        },
        getLatestData: (state) => (cardId) => {
            const card = state.cardList.find((card) => card.id === cardId)
            const data = {}
            card.data.forEach((item) => {
                data[item.id] = state.latestData[item.id]
            })
            return data
        },
        getSelectedData: (state, getters) => (dataId) => {
            const val = getters.getHistoryDataByDate(dataId, state.selectedDate)
            // round val if is a fp number
            if (typeof val === 'number') {
                const card = state.cardList.find((card) =>
                    card.data.find((item) => item.id === dataId)
                )
                const round = card.data.find((item) => item.id === dataId).round ?? 2
                return Math.round(val * 10 ** round) / 10 ** round
            }
            return val
        },
        // 目前選定時間點的微電網資料是否為設備斷線。
        // 用 mock 時恆為 false，否則假資料也會被判成斷線。
        microgridOffline: (state, getters) => {
            if (USE_MICROGRID_MOCK) return false
            const sample = {}
            MICROGRID_FIELDS.forEach((field) => {
                sample[field] = getters.getHistoryDataByDate(field, state.selectedDate)
            })
            return isMicrogridOffline(sample)
        },
        // 目前資料視窗內最後一筆有讀到資料的時間。
        // 設備中斷超過視窗長度時為 null，畫面改為只顯示中斷、不附時間。
        microgridLastOnline: (state) => {
            if (USE_MICROGRID_MOCK) return null
            return findLastOnlineTime(state.historyData)
        },
        getHistoryDataByDate: (state) => (dataId, dateObj) => {
            if (!state.historyData['DateTime']) return null
            const timeIdx = state.historyData['DateTime'].findIndex((timeStr) => {
                const date = new Date(timeStr)
                return (
                    date.getDate() === dateObj.getDate() &&
                    date.getHours() === dateObj.getHours() &&
                    date.getMinutes() === dateObj.getMinutes()
                )
            })
            if (timeIdx === -1) return null
            return state.historyData[dataId][timeIdx]
        },
        getChartTimeInterval: (state) => () => {
            return state.chartTimeInterval // should be 30 or 60
        },
        getChartTimeSeries: (state) => () => {
            const times = state.historyData['DateTime']
            if (!times) return []
            // 放大後的 24 小時會跨過午夜，全部只印 HH:MM 的話讀者分不出哪幾格是昨天。
            // 午夜那一格改印日期當分界，其餘維持 HH:MM。
            return chartSampleIndices(state).map((index) => {
                const dateObj = new Date(times[index])
                const hh = dateObj.getHours().toString().padStart(2, '0')
                const mm = dateObj.getMinutes().toString().padStart(2, '0')
                if (hh === '00' && mm === '00') {
                    return `${dateObj.getMonth() + 1}/${dateObj.getDate()}`
                }
                return `${hh}:${mm}`
            })
        },
        getDatasets: (state) => (cardId) => {
            const card = state.cardList.find((card) => card.id === cardId)
            // filter only data that has chart settings
            const dataList = card.data.filter((data) => data.chartSettings)

            const indices = chartSampleIndices(state)

            return dataList.map((data) => {
                if (!state.historyData[data.id]) return data.chartSettings
                return {
                    ...data.chartSettings,
                    data: indices.map((index) => state.historyData[data.id][index]),
                }
            })
        },
        getGPSPivot: (state) => () => {
            return state.gpsPivot
        },
    },
    mutations: {
        // methods
        // dynamic data
        setSelectedDate(state, dateObj) {
            state.selectedDate = dateObj
        },
        setSelectedStation(state, station) {
            state.selectedStation = station
            // 【新增】重置卡片輪播索引 (歸零)
            // 這能確保切換回來時，一定是從第一張開始顯示，不會卡在中間或奇怪的位置
            state.firstCardIndex = {
                small: 0,
                medium: 0,
            }
            // 【新增】如果是三光國小 (sg)，強制放大傾斜儀 (tilt) 卡片
            if (station === 'sg') {
                state.largeCardId = 'tilt'
            } else {
                // 如果切回其他站點，記得取消放大，回復預設總覽模式
                state.largeCardId = 'None'
            }
        },
        setLatestData(state, latestData) {
            state.latestData = latestData
        },
        setHistoryData(state, historyData) {
            state.historyData = historyData
        },
        setChartTimeInterval(state, interval) {
            state.chartTimeInterval = interval
        },
        setTimeSeries(state, timeSeriesList) {
            state.timeSeriesList = timeSeriesList
        },
        setGPSPivot(state, pivot) {
            state.gpsPivot = pivot
        },
        setMobileCardId(state, cardId) {
            state.mobileCardId = cardId
        },
        // layout
        setLargeCardId(state, largeCardId) {
            state.largeCardId = largeCardId
            // 如果是三光國小 (sg)，強制鎖定為 tilt，不允許切換回 None (小卡模式)
            if (state.selectedStation === 'sg') {
                state.largeCardId = 'tilt'
                return
            }
        },
        setFirstCardIndex(state, { cardSize, firstCardIndex }) {
            state.firstCardIndex[cardSize] = firstCardIndex
        },
        setHistoryFetched(state, fetched) {
            state.historyFetched = fetched
        },
    },
    actions: {
        swipeCard({ state, commit, getters }, { cardSize, direction }) {
            const cardListLength = getters.getCardList(cardSize).length
            const firstIndex = state.firstCardIndex[cardSize]

            let newIndex = firstIndex
            if (direction === 'left') {
                newIndex += cardListLength - 1
            } else if (direction === 'right') {
                newIndex += 1
            }
            newIndex %= cardListLength

            commit('setFirstCardIndex', {
                cardSize: cardSize,
                firstCardIndex: newIndex,
            })
        },
        // async methods
        // dynamic data
        async fetchLatestData({ commit, state }) {
            try {
                let url = new URL(`${API_BASE}/latest/`)
                const params = new URLSearchParams({
                    location: resolveDataLocation(state.selectedStation),
                })
                url.search = params.toString()
                const response = await fetch(url)
                if (!response.ok) return
                const latestData = await response.json()
                commit('setLatestData', latestData)
            } catch (e) {
                console.warn('fetchLatestData failed:', e)
            }
        },
        async fetchHistoryData({ commit, state }, date = state.selectedDate) {
            commit('setHistoryFetched', false)
            try {
                let url = new URL(`${API_BASE}/history/`)
                let queryDateStr = new Date(
                    date.getTime() - date.getTimezoneOffset() * 60000
                ).toISOString()

                const params = new URLSearchParams({
                    before: queryDateStr,
                    limit: HISTORY_LIMIT,
                    location: resolveDataLocation(state.selectedStation),
                })
                url.search = params.toString()
                const response = await fetch(url)
                if (!response.ok) {
                    commit('setHistoryFetched', true)
                    return
                }
                const historyData = await response.json()
                const groupedData = {}
                const allDataIds = state.cardList
                    .map((card) => card.data.map((item) => item.id))
                    .flat()
                allDataIds.push('DateTime')
                allDataIds.forEach((id) => {
                    groupedData[id] = historyData.map((item) => item[id])
                })

                groupedData['DateTime'] = groupedData['DateTime'].map((item) => new Date(item))
                Object.keys(groupedData).forEach((key) => {
                    groupedData[key].reverse()
                })

                // 設備斷線期間若需展示完整畫面，可在 constants.js 把開關打開改用 mock。
                if (USE_MICROGRID_MOCK) {
                    Object.assign(groupedData, buildMicrogridColumns(groupedData['DateTime']))
                } else {
                    // 斷線的樣本改為 null，折線圖才會斷開而非畫出一條貼底的假線
                    maskOfflineSamples(groupedData)
                }
                addGreenRatio(groupedData)
                addCarbonImpact(groupedData)

                commit('setHistoryData', groupedData)
                commit('setHistoryFetched', true)
            } catch (e) {
                console.warn('fetchHistoryData failed:', e)
                // 仍標記為已完成，避免 UI 卡在載入狀態
                commit('setHistoryFetched', true)
            }
        },
        async fetchTimeSeriesList({ commit, state }) {
            try {
                let url = new URL(`${API_BASE}/date/`)
                let dateObj = new Date(state.selectedDate)
                let queryDate = dateObj
                    .toLocaleDateString([], {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                    })
                    .replaceAll('/', '-')
                const params = new URLSearchParams({
                    date: queryDate,
                    location: resolveDataLocation(state.selectedStation),
                })
                url.search = params.toString()
                const response = await fetch(url)
                if (!response.ok) return
                const timeSeries = await response.json()

                const timeSeriesList = []
                timeSeries.forEach(([hour, minute]) => {
                    timeSeriesList.push(
                        `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
                    )
                })
                commit('setTimeSeries', timeSeriesList)
            } catch (e) {
                console.warn('fetchTimeSeriesList failed:', e)
            }
        },
        async fetchGPSPivot({ commit, state }) {
            try {
                let url = new URL(`${API_BASE}/gps_pivot/`)
                const params = new URLSearchParams({
                    location: resolveDataLocation(state.selectedStation),
                    date: state.selectedDate,
                    avg_length: 672, // 7 * 24 * 4
                })
                url.search = params.toString()
                const response = await fetch(url)
                if (!response.ok) return
                const pivot = await response.json()
                commit('setGPSPivot', pivot)
            } catch (e) {
                console.warn('fetchGPSPivot failed:', e)
            }
        },
    },
    modules: {},
})
